import { useEffect, useState } from "react";
import styles from "./driverJobDelivery.module.css";
import dayjs from "dayjs";
import { Button, Table, TableCell, TableHead, TableContainer, Paper, TableBody, TableRow, Snackbar, Alert, Checkbox, Dialog, Tooltip } from "@mui/material";
import { CheckCircleOutline, FileDownloadOutlined, PrintOutlined } from "@mui/icons-material";
import ListToolbar from "../../components/ListToolbar/ListToolbar";
import { downloadCsv } from "../../components/ListToolbar/csv";
import TablePaginationComponent from "../../components/TablePagination";
import { ArrowForwardOutlined } from "@mui/icons-material";
import axios from "axios";
import Loader from "../../components/Loader";
import { authData, withAdminAuth } from "../../components/GetAuth";
import { ComponentToPrint } from "./driverJobDeliveryPrint";
import { useReactToPrint, ReactToPrint, PrintContextConsumer } from "react-to-print";
import { ChevronDown, ChevronUp, Edit3, Plus } from "react-feather";
import { useRef } from "react";
import { useWebsite } from "../../../context/WebsiteContext";
import { useSiteBranding } from "../../../hooks/useSiteBranding";
import PageHeader from "../../components/PageHeader";
import AddDriverForm from "../../components/popups/AddDriverForm";
import UpdateNotePopup from "../../components/popups/UpdateNotePopup";
import ReassignDeliveryDriverPopup from "../../components/popups/ReassignDeliveryDriverPopup";
import DeliveryTableLoader from "./DeliveryTableLoader";

// A booking's out_for_delivery_items may span more than one driver (assigned
// in separate batches). This returns the single driver name when they're
// all the same, otherwise a generic "Multiple drivers" label.
const driverSummary = (items) => {
  const names = Array.from(new Set((items || []).map((i) => i?.delivery_driver?.name).filter(Boolean)));
  if (names.length === 0) return "-";
  if (names.length === 1) return names[0];
  return `${names.length} drivers`;
};

const JOB_EXPORT_COLUMNS = [
  { label: "Driver Name", value: (r) => r.item?.delivery_driver?.name },
  { label: "Booking Id", value: (r) => r.booking?.booking_id },
  { label: "Item", value: (r) => r.item?.product_title },
  { label: "Address", value: (r) => r.booking?.recipient_details?.address },
  { label: "Contact No", value: (r) => r.booking?.recipient_details?.phone },
  { label: "Recipient Name", value: (r) => r.booking?.recipient_details?.name },
  { label: "Order Status", value: (r) => (parseInt(r.booking?.payment_status) === 1 ? "Paid" : "Unpaid") },
  { label: "Delivery Notes", value: (r) => r.booking?.delivery_note },
  { label: "Assigned Date", value: (r) => dayjs(r.item?.updated_at).format("DD/MM/YYYY") },
];

// Search · selection · Mark delivered · ⋯ (Export CSV, Print selected jobs) in one row
const JobsToolbar = ({ data, selected, setSelected, filterVars, setFilterVars, onMarkDelivered }) => {
  const printRef = useRef();
  const handlePrint = useReactToPrint({ content: () => printRef.current });

  // One CSV row per item: driver and assigned date belong to the item, not the booking
  const exportCsv = () => downloadCsv("driver-jobs.csv", JOB_EXPORT_COLUMNS, (data || []).flatMap((booking) => (booking?.out_for_delivery_items || []).map((item) => ({ booking, item }))));

  // The job sheet prints the bookings that have a ticked item
  const selectedBookings = data?.filter((booking) => (booking?.out_for_delivery_items || []).some((item) => selected.indexOf(item.id) > -1));

  return (<>
    <ListToolbar
      search={filterVars?.searchKey}
      onSearch={(value) => setFilterVars((prev) => ({ ...prev, searchKey: value }))}
      searchPlaceholder="Search driver, booking, recipient..."
      showDate={false}
      selectedCount={selected.length}
      onClearSelection={() => setSelected([])}
      primaryAction={{ label: selected.length ? `Mark delivered (${selected.length})` : "Mark delivered", icon: <CheckCircleOutline />, onClick: onMarkDelivered, disabled: selected.length === 0, hint: "Tick the delivered items first" }}
      menuItems={[
        { label: "Export CSV", icon: <FileDownloadOutlined fontSize="small" />, onClick: exportCsv, disabled: !data?.length },
        { label: selected.length ? `Print selected jobs (${selected.length})` : "Print selected jobs", icon: <PrintOutlined fontSize="small" />, onClick: handlePrint, disabled: selected.length === 0 },
      ]}
    />
    <div style={{ display: "none" }}>
      <ComponentToPrint ref={printRef} bookingData={selectedBookings} />
    </div>
  </>);
};

const DriverJobDelivery = () => {
  const auth = authData();
  const componentRef = useRef();
  const API_TOKEN = auth?.api_token;
  const [loading, setLoading] = useState(false);
  const [contentLoading, setContentLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [allData, setAllData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [filterVars, setFilterVars] = useState({ searchKey: "", date: null });
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarType, setSnackbarType] = useState("success");
  // `selected` now holds booking_product (item) ids, not booking ids.
  const [selected, setSelected] = useState([]);
  const [formOpen, setFormOpen] = useState(false);
  const [driverFormOpen, setDriverFormOpen] = useState(false);
  const [selectedItemIds, setSelectedItemIds] = useState([]);
  const [noteModalOpen, setNoteModalOpen] = useState(false);
  const [selectBooking, setSelectBooking] = useState(null);
  const [noteData, setNoteData] = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  const { websiteData } = useWebsite();
  useSiteBranding("Delivery Driver Job", websiteData);

  const paginationChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const paginationChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const fetchBookings = () => {
    setContentLoading(true);
    axios
      .get(process.env.REACT_APP_API_URL + "v1/delivery/assigned-bookings", {
        headers: {
          Authorization: `Bearer ${API_TOKEN}`,
        },
      })
      .then((response) => {
        setContentLoading(false);
        setAllData(response.data.data);
      })
      .catch((error) => {
        setContentLoading(false);
      });
  };

  useEffect(() => {
    fetchBookings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [API_TOKEN]);

  useEffect(() => {
    let filterredTempData = allData;

    if (filterVars?.searchKey !== "") {
      let inputVal = filterVars?.searchKey;
      inputVal = inputVal.toLowerCase();
      filterredTempData = filterredTempData.filter((item) => {
        return (
          driverSummary(item?.out_for_delivery_items).toLowerCase().includes(inputVal) ||
          item?.booking_id.toLowerCase().includes(inputVal) ||
          item?.recipient_details?.address.toLowerCase().includes(inputVal) ||
          item?.recipient_details?.phone.toLowerCase().includes(inputVal) ||
          item?.recipient_details?.name.toLowerCase().includes(inputVal)
        );
      });
    }

    setFilteredData(filterredTempData);
  }, [allData, filterVars]);

  const toggleItem = (itemId) => {
    setSelected((prev) => prev.indexOf(itemId) > -1
      ? prev.filter((id) => id !== itemId)
      : [...prev, itemId]);
  };

  const toggleBookingItems = (booking) => {
    const ids = (booking?.out_for_delivery_items || []).map((i) => i.id);
    if (ids.length === 0) return;

    setSelected((prev) => {
      const allSelected = ids.every((id) => prev.indexOf(id) > -1);
      return allSelected
        ? prev.filter((id) => ids.indexOf(id) === -1)
        : Array.from(new Set([...prev, ...ids]));
    });
  };

  const allEligibleIds = filteredData?.flatMap((b) => (b?.out_for_delivery_items || []).map((i) => i.id)) || [];
  const allSelected = allEligibleIds.length > 0 && allEligibleIds.every((id) => selected.indexOf(id) > -1);
  const someSelected = allEligibleIds.some((id) => selected.indexOf(id) > -1);

  const markDelivered = () => {
    if (selected.length === 0) {
      setSnackbarType("error");
      setSnackbarOpen(true);
      setSnackbarMessage("Please select item(s)");

      return false;
    }

    setLoading(true);
    let postData = { item_ids: selected };

    axios
      .post(process.env.REACT_APP_API_URL + "v1/delivery/mark-delivered-items", postData, {
        headers: {
          Authorization: `Bearer ${API_TOKEN}`,
        },
      })
      .then((response) => {
        setLoading(false);
        if (response.data.status === "SUCCESS") {
          setSnackbarOpen(true);
          setSnackbarMessage(response.data.message);
          setSnackbarType("success");
          setSelected([]);
          fetchBookings();
        } else {
          setSnackbarOpen(true);
          setSnackbarMessage(response.data.message || "Something went wrong");
          setSnackbarType("error");
        }
      })
      .catch((error) => {
        setLoading(false);
      });
  };

  const onAssign = () => {
    setSnackbarType("success");
    setSnackbarOpen(true);
    setSnackbarMessage("Driver assigned successfully.");
    setLoading(false);
    fetchBookings();
  };

  const isPaid = (item) => item?.payment_status === 1 || item?.payment_status === "1";

  const pagedData = filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <>
      <Loader open={loading} setOpen={setLoading.bind(this)} />
      <div className={styles.MainDiv}>
        <PageHeader title="Delivery" trail={[{ label: "Driver Job" }]} />
        <div className={styles.MainCard}>
          {contentLoading && <DeliveryTableLoader rows={rowsPerPage} />}

          {!contentLoading && (
            <>
              <JobsToolbar data={filteredData} selected={selected} setSelected={setSelected} filterVars={filterVars} setFilterVars={setFilterVars} onMarkDelivered={markDelivered} />

              {/* ============ Desktop table ============ */}
              <div className={styles.CusTable}>
                <TableContainer component={Paper} elevation={0}>
                  <Table sx={{ minWidth: 650 }} aria-label="driver job delivery table">
                    <TableHead>
                      <TableRow>
                        <TableCell className={styles.TableHeaderCell} padding="checkbox">
                          <Checkbox
                            color="primary"
                            indeterminate={someSelected && !allSelected}
                            checked={allSelected}
                            disabled={allEligibleIds.length === 0}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelected((prev) => Array.from(new Set([...prev, ...allEligibleIds])));
                                return;
                              }
                              setSelected((prev) => prev.filter((id) => allEligibleIds.indexOf(id) === -1));
                            }}
                          />
                        </TableCell>
                        <TableCell className={styles.TableHeaderCell}>Driver</TableCell>
                        <TableCell className={styles.TableHeaderCell}>Booking Id</TableCell>
                        <TableCell className={styles.TableHeaderCell}>Address</TableCell>
                        <TableCell className={styles.TableHeaderCell}>Contact</TableCell>
                        <TableCell className={styles.TableHeaderCell}>Recipient</TableCell>
                        <TableCell className={styles.TableHeaderCell}>Status</TableCell>
                        <TableCell className={styles.TableHeaderCell}>Delivery Note</TableCell>
                        <TableCell className={styles.TableHeaderCell}>Items</TableCell>
                        <TableCell className={styles.TableHeaderCell}>Actions</TableCell>
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {pagedData.map((item) => {
                        const items = item?.out_for_delivery_items || [];
                        const itemIds = items.map((i) => i.id);
                        const bookingAllSelected = itemIds.length > 0 && itemIds.every((id) => selected.indexOf(id) > -1);
                        const bookingSomeSelected = itemIds.some((id) => selected.indexOf(id) > -1);
                        const isExpanded = expandedId === item.id;

                        return (
                          <>
                            <TableRow key={item?.id} className={styles.TableRow}>
                              <TableCell padding="checkbox">
                                <Checkbox
                                  color="primary"
                                  indeterminate={bookingSomeSelected && !bookingAllSelected}
                                  checked={bookingAllSelected}
                                  disabled={itemIds.length === 0}
                                  onClick={(e) => toggleBookingItems(item)}
                                />
                              </TableCell>
                              <TableCell>{driverSummary(items)}</TableCell>
                              <TableCell>{item?.booking_id}</TableCell>
                              <TableCell>{item?.recipient_details?.address}</TableCell>
                              <TableCell>{item?.recipient_details?.phone}</TableCell>
                              <TableCell>{item?.recipient_details?.name}</TableCell>
                              <TableCell>
                                <span className={`${styles.StatusBU} ${isPaid(item) ? styles.DisaBU : ""}`}>
                                  {isPaid(item) ? "Paid" : "Unpaid"}
                                </span>
                              </TableCell>
                              <TableCell>
                                {item?.delivery_note ? (
                                  <>
                                    <p className={styles.NoteText}>{item?.delivery_note}</p>
                                    <button className={styles.AddNote} onClick={(e) => { e.preventDefault(); setNoteModalOpen(true); setNoteData(item?.delivery_note); setSelectBooking(item); }}>
                                      <Edit3 size={12} /> Update
                                    </button>
                                  </>
                                ) : (
                                  <button className={styles.AddNote} onClick={(e) => { e.preventDefault(); setNoteModalOpen(true); setNoteData(null); setSelectBooking(item); }}>
                                    <Plus /> Add note
                                  </button>
                                )}
                              </TableCell>
                              <TableCell>
                                <div className={styles.ItemCountStack}>
                                  <span className={`${styles.CountTag} ${styles.CountOut}`}>{item?.out_for_delivery_item_count ?? items.length} out</span>
                                  {item?.delivered_item_count > 0 && <span className={`${styles.CountTag} ${styles.CountDone}`}>{item.delivered_item_count} delivered</span>}
                                  <span className={styles.CountTotal}>/ {item?.total_item_count ?? "?"} total</span>
                                </div>
                                <button className={styles.ExpandToggle} onClick={(e) => setExpandedId(isExpanded ? null : item.id)}>
                                  View items {isExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                                </button>
                              </TableCell>
                              <TableCell>
                                <div className={styles.ActionDiv}>
                                  <Tooltip title="Print">
                                    <span>
                                      <div style={{ display: "none" }}>
                                        <ComponentToPrint ref={(el) => (componentRef[item?.id] = el)} bookingData={[item]} />
                                      </div>
                                      <ReactToPrint content={() => componentRef[item?.id]}>
                                        <PrintContextConsumer>
                                          {({ handlePrint }) => (
                                            <Button className={styles.ActionBU} onClick={handlePrint}>
                                              <PrintOutlined />
                                            </Button>
                                          )}
                                        </PrintContextConsumer>
                                      </ReactToPrint>
                                    </span>
                                  </Tooltip>
                                </div>
                              </TableCell>
                            </TableRow>

                            {isExpanded && (
                              <TableRow>
                                <TableCell colSpan={9} className={styles.ItemPanelCell}>
                                  <div className={styles.ItemPanel}>
                                    {items.map((prod) => {
                                      const isChecked = selected.indexOf(prod.id) > -1;
                                      return (
                                        <div className={styles.ItemRow} key={prod.id}>
                                          <Checkbox
                                            size="small"
                                            color="primary"
                                            checked={isChecked}
                                            onChange={() => toggleItem(prod.id)}
                                          />
                                          <div className={styles.ItemRowTitle}>{prod?.product_title}</div>
                                          <span className={styles.ItemDriverTag}>{prod?.delivery_driver?.name || "Unassigned"}</span>
                                          <Tooltip title="Re-assign this item">
                                            <Button
                                              className={styles.ActionBU}
                                              onClick={() => {
                                                setFormOpen(true);
                                                setSelectedItemIds([prod.id]);
                                              }}
                                            >
                                              <ArrowForwardOutlined fontSize="small" />
                                            </Button>
                                          </Tooltip>
                                        </div>
                                      );
                                    })}
                                    {items.length === 0 && <p className={styles.ItemPanelEmpty}>No items currently out for delivery.</p>}
                                  </div>
                                </TableCell>
                              </TableRow>
                            )}
                          </>
                        );
                      })}
                      {filteredData.length === 0 && (
                        <TableRow>
                          <TableCell className={styles.EmptyCell} colSpan={10}>
                            No deliveries found
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>

                  </Table>
                </TableContainer>

                {filteredData?.length > 0 && (
                  <div className={styles.PaginationWrap}>
                    <TablePaginationComponent
                      count={filteredData?.length}
                      page={page}
                      rowsPerPage={rowsPerPage}
                      handleChangePage={paginationChangePage}
                      handleChangeRowsPerPage={paginationChangeRowsPerPage}
                    />
                  </div>
                )}
              </div>

              {/* ============ Mobile card list ============ */}
              <div className={styles.CardList}>
                {pagedData.map((item) => {
                  const items = item?.out_for_delivery_items || [];
                  const itemIds = items.map((i) => i.id);
                  const bookingSomeSelected = itemIds.some((id) => selected.indexOf(id) > -1);

                  return (
                    <div
                      key={item?.id}
                      className={`${styles.BookingCard} ${bookingSomeSelected ? styles.CardSelected : ""}`}
                    >
                      <div className={styles.CardTopRow}>
                        <Checkbox
                          className={styles.CardCheckbox}
                          color="primary"
                          checked={itemIds.length > 0 && itemIds.every((id) => selected.indexOf(id) > -1)}
                          indeterminate={bookingSomeSelected && !itemIds.every((id) => selected.indexOf(id) > -1)}
                          onClick={(e) => toggleBookingItems(item)}
                        />
                        <div className={styles.CardHeading}>
                          <p className={styles.CardBookingId}>{item?.booking_id}</p>
                          <p className={styles.CardDriverName}>{driverSummary(items)}</p>
                        </div>
                        <span className={`${styles.StatusBU} ${isPaid(item) ? styles.DisaBU : ""}`}>
                          {isPaid(item) ? "Paid" : "Unpaid"}
                        </span>
                      </div>

                      <div className={styles.CardGrid}>
                        <div className={styles.CardField}>
                          <span className={styles.CardFieldLabel}>Recipient</span>
                          <span className={styles.CardFieldValue}>{item?.recipient_details?.name}</span>
                        </div>
                        <div className={styles.CardField}>
                          <span className={styles.CardFieldLabel}>Contact</span>
                          <span className={styles.CardFieldValue}>{item?.recipient_details?.phone}</span>
                        </div>
                        <div className={styles.CardField}>
                          <span className={styles.CardFieldLabel}>Address</span>
                          <span className={styles.CardFieldValue}>{item?.recipient_details?.address}</span>
                        </div>
                        <div className={styles.CardField}>
                          <span className={styles.CardFieldLabel}>Items</span>
                          <span className={styles.CardFieldValue}>
                            {item?.out_for_delivery_item_count ?? items.length} out
                            {item?.delivered_item_count > 0 ? ` · ${item.delivered_item_count} delivered` : ""}
                            {" "}/ {item?.total_item_count ?? "?"} total
                          </span>
                        </div>
                      </div>

                      <div className={styles.ItemPanel}>
                        {items.map((prod) => {
                          const isChecked = selected.indexOf(prod.id) > -1;
                          return (
                            <div className={styles.ItemRow} key={prod.id}>
                              <Checkbox size="small" color="primary" checked={isChecked} onChange={() => toggleItem(prod.id)} />
                              <div className={styles.ItemRowTitle}>{prod?.product_title}</div>
                              <span className={styles.ItemDriverTag}>{prod?.delivery_driver?.name || "Unassigned"}</span>
                            </div>
                          );
                        })}
                      </div>

                      <div className={styles.CardFooterRow}>
                        {item?.delivery_note ? (
                          <div>
                            <p className={styles.NoteText}>{item?.delivery_note}</p>
                            <button
                              className={styles.AddNote}
                              onClick={(e) => {
                                e.preventDefault();
                                setNoteModalOpen(true);
                                setNoteData(item?.delivery_note);
                                setSelectBooking(item);
                              }}
                            >
                              Update note
                            </button>
                          </div>
                        ) : (
                          <button
                            className={styles.AddNote}
                            onClick={(e) => {
                              e.preventDefault();
                              setNoteModalOpen(true);
                              setNoteData(null);
                              setSelectBooking(item);
                            }}
                          >
                            <Plus /> Add note
                          </button>
                        )}

                        <div className={styles.CardActions}>
                          <div style={{ display: "none" }}>
                            <ComponentToPrint ref={(el) => (componentRef[item?.id] = el)} bookingData={[item]} />
                          </div>
                          <ReactToPrint content={() => componentRef[item?.id]}>
                            <PrintContextConsumer>
                              {({ handlePrint }) => (
                                <Button className={styles.ActionBU} onClick={handlePrint}>
                                  <PrintOutlined />
                                </Button>
                              )}
                            </PrintContextConsumer>
                          </ReactToPrint>
                        </div>
                      </div>
                    </div>
                  );
                })}

                {filteredData.length === 0 && <div className={styles.EmptyState}>No deliveries found</div>}

                {filteredData?.length > 0 && (
                  <div className={styles.PaginationWrap}>
                    <TablePaginationComponent
                      count={filteredData?.length}
                      page={page}
                      rowsPerPage={rowsPerPage}
                      handleChangePage={paginationChangePage}
                      handleChangeRowsPerPage={paginationChangeRowsPerPage}
                    />
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      <Dialog open={formOpen} onClose={(e) => setFormOpen(false)} fullWidth maxWidth="sm">
        <ReassignDeliveryDriverPopup
          setLoading={setLoading.bind(this)}
          modalClose={setFormOpen.bind(this)}
          itemIds={selectedItemIds}
          onSubmit={onAssign.bind(this)}
        />
      </Dialog>

      <Dialog open={driverFormOpen} onClose={(e) => setFormOpen(false)} fullWidth maxWidth="sm">
        <AddDriverForm
          setFormOpen={setDriverFormOpen.bind(this)}
          setAllData={() => fetchBookings()}
          setSnackbarOpen={setSnackbarOpen.bind(this)}
          setSnackbarMessage={setSnackbarMessage.bind(this)}
          setSnackbarType={setSnackbarType.bind(this)}
          setLoading={setLoading.bind(this)}
          editData={null}
        />
      </Dialog>

      <Dialog fullWidth maxWidth="sm" open={noteModalOpen} onClose={(e) => setNoteModalOpen(false)}>
        <UpdateNotePopup
          setNoteModalOpen={setNoteModalOpen.bind(this)}
          noteData={noteData}
          selectBooking={selectBooking}
          setLoading={setLoading.bind(this)}
          setAllData={setAllData.bind(this)}
          setSnackbarOpen={setSnackbarOpen.bind(this)}
          setSnackbarMessage={setSnackbarMessage.bind(this)}
          setSnackbarType={setSnackbarType.bind(this)}
        />
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={(e) => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert onClose={(e) => setSnackbarOpen(false)} severity={snackbarType} variant="filled" sx={{ width: "100%" }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default withAdminAuth(DriverJobDelivery);
